import React, { useState, useEffect } from 'react';
import { Clock, TrendingUp, Calendar, Lightbulb, Loader } from 'lucide-react';
import emailService, { SendTimeRecommendation } from '../../../services/emailService';

interface SendTimeOptimizerProps {
  onSelectTime?: (day: string, hour: number) => void;
  campaignSize?: number;
}

const SendTimeOptimizer: React.FC<SendTimeOptimizerProps> = ({
  onSelectTime,
  campaignSize
}) => {
  const [recommendations, setRecommendations] = useState<SendTimeRecommendation[]>([]);
  const [bestTime, setBestTime] = useState<SendTimeRecommendation | null>(null);
  const [insights, setInsights] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    loadRecommendations();
  }, []);

  const loadRecommendations = async () => {
    setIsLoading(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const data = await emailService.getSendTimeRecommendations(timezone);
      setRecommendations(data.recommendations || []);
      setBestTime(data.best_overall || null);
      setInsights(data.insights || []);
    } catch (error) {
      console.error('Failed to load recommendations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatHour = (hour: number) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 0.8) return { color: 'bg-green-100 text-green-800', label: 'High' };
    if (confidence >= 0.6) return { color: 'bg-yellow-100 text-yellow-800', label: 'Medium' };
    return { color: 'bg-orange-100 text-orange-800', label: 'Low' };
  };

  if (isLoading) {
    return (
      <div className="border rounded-lg p-6 bg-white">
        <div className="flex items-center justify-center py-8">
          <Loader className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Analyzing your campaign data...</span>
        </div>
      </div>
    );
  }

  if (!bestTime) {
    return (
      <div className="border rounded-lg p-6 bg-white">
        <div className="text-center py-8">
          <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 mb-2">Not enough data yet</p>
          <p className="text-sm text-gray-500">Send more campaigns to get personalized recommendations</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg bg-white overflow-hidden">
      {/* Best Time Card */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 border-b">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Optimal Send Time</h3>
            </div>
            
            <div className="flex items-baseline gap-3 mb-3">
              <div>
                <p className="text-3xl font-bold text-blue-600">{bestTime.best_day}</p>
                <p className="text-sm text-gray-600">Best Day</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-purple-600">{formatHour(bestTime.best_hour)}</p>
                <p className="text-sm text-gray-600">Best Time</p>
              </div>
            </div>

            <div className="flex gap-4 text-sm">
              <div>
                <span className="text-gray-600">Open Rate:</span>
                <span className="ml-1 font-semibold text-green-600">
                  {(bestTime.open_probability * 100).toFixed(1)}%
                </span>
              </div>
              <div>
                <span className="text-gray-600">Reply Rate:</span>
                <span className="ml-1 font-semibold text-blue-600">
                  {(bestTime.reply_probability * 100).toFixed(1)}%
                </span>
              </div>
              <div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getConfidenceBadge(bestTime.confidence).color}`}>
                  {getConfidenceBadge(bestTime.confidence).label} Confidence
                </span>
              </div>
            </div>
          </div>

          {onSelectTime && (
            <button
              onClick={() => onSelectTime(bestTime.best_day, bestTime.best_hour)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 whitespace-nowrap"
            >
              Use This Time
            </button>
          )}
        </div>
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div className="p-6 border-b bg-blue-50">
          <div className="flex items-start gap-2">
            <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 mb-2">Insights from your data:</h4>
              <ul className="space-y-1">
                {insights.map((insight, i) => (
                  <li key={i} className="text-sm text-gray-700">• {insight}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Alternative Times */}
      {recommendations.length > 1 && (
        <div className="p-6">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center justify-between w-full text-left mb-4"
          >
            <h4 className="font-medium text-gray-900">Alternative Send Times</h4>
            <span className="text-sm text-blue-600">
              {showDetails ? 'Hide' : 'Show'} top {recommendations.length} times
            </span>
          </button>

          {showDetails && (
            <div className="space-y-2">
              {recommendations.map((rec, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 border rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
                  onClick={() => onSelectTime?.(rec.best_day, rec.best_hour)}
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <Calendar className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                      <p className="text-sm font-medium text-gray-900">{rec.best_day}</p>
                      <p className="text-xs text-gray-500">{formatHour(rec.best_hour)}</p>
                    </div>
                    <div className="text-sm">
                      <p className="text-gray-900">
                        <span className="font-medium">{(rec.open_probability * 100).toFixed(1)}%</span> open rate
                      </p>
                      <p className="text-gray-600">
                        Based on {rec.data_points} sends
                      </p>
                    </div>
                  </div>
                  <div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getConfidenceBadge(rec.confidence).color}`}>
                      {getConfidenceBadge(rec.confidence).label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SendTimeOptimizer;
